import { createUID, type IconProps } from '@pega/cosmos-react-core';
import { getIconMapping, typeIconMapping, type FieldType } from '@pega/cosmos-react-build';

export type ContentType = 'Field' | 'View' | 'Group';

// A simple mock of an object type before it is transformed to a ContentItem
export interface MockRawItem {
  id: string;
  label: string;
  type: ContentType;
  subType?: FieldType;
  icon?: IconProps;
  items?: MockRawItem[];
}

// Used as the arg type to the simulated query function.
export interface MockQueryParams {
  id?: MockRawItem['id'] | MockRawItem['id'][];
  filter?: string;
  type?: ContentType;
  subType?: FieldType;
  limit?: number;
  offset?: number;
  delay?: number;
}

const getRandomInt = (min: number, max: number) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const mockFields: MockRawItem[] = Array.from({ length: 500 }, (_, i) => {
  const types = Object.keys(typeIconMapping) as (keyof typeof typeIconMapping)[];
  const subType = types[getRandomInt(0, types.length - 1)];

  return {
    id: createUID(),
    label: `Field-${i + 1}`,
    type: 'Field',
    icon: getIconMapping(subType),
    selected: i === 0,
    subType
  };
});

export const mockViews: MockRawItem[] = Array.from({ length: 50 }, (_, i) => {
  return {
    id: createUID(),
    label: `View-${i + 1}`,
    icon: getIconMapping('View'),
    type: 'View'
  };
});

export const mockGroups: MockRawItem[] = Array.from({ length: 3 }, (_, i) => {
  return {
    id: createUID(),
    label: `Group-${i + 1}`,
    icon: getIconMapping('Group'),
    type: 'Group',
    items: i === 0 ? mockFields.slice(-3) : mockFields.slice((i + 1) * -3, i * -3)
  };
});

// Simulated query functionality to assist with loading states and fetching specifc content item types.
export const mockQuery = ({
  id,
  filter,
  type,
  subType,
  limit = 100,
  offset = 0,
  delay = 0
}: MockQueryParams = {}): Promise<MockRawItem[]> => {
  return new Promise(resolve => {
    setTimeout(() => {
      let contentItems: MockRawItem[] = [];

      if (id) {
        resolve(
          [...mockFields, ...mockViews, ...mockGroups].filter(item => {
            return Array.isArray(id) ? id.includes(item.id) : item.id === id;
          })
        );
        return;
      }

      if (type) {
        if (type === 'Field') {
          contentItems = subType ? mockFields.filter(f => f.subType === subType) : mockFields;
        } else if (type === 'View') {
          contentItems = mockViews;
        } else if (type === 'Group') {
          contentItems = mockGroups;
        }
      } else {
        contentItems = [...mockFields, ...mockViews, ...mockGroups];
      }

      if (filter) {
        contentItems = contentItems.filter(item => item.label.includes(filter));
      }

      resolve(contentItems.slice(offset, limit));
    }, delay);
  });
};
